import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import LooxsEmblem from "@/components/LooxsEmblem";

export default function Collections() {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [params, setParams] = useSearchParams();
  const gender = params.get("gender") || "";
  const category = params.get("category") || "";

  useEffect(() => { api.get("/collections").then(r => setCollections(r.data)); }, []);
  useEffect(() => {
    const q = {};
    if (gender) q.gender = gender;
    if (category) q.category = category;
    api.get("/products", { params: q }).then(r => setProducts(r.data));
  }, [gender, category]);

  const setFilter = (k, v) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n);
  };

  return (
    <div className="pt-32 pb-24 bg-ivory text-ink">
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <div className="text-center mb-20">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-6">The Wardrobe</p>
          <h1 className="font-editorial text-5xl md:text-7xl leading-[0.95] tracking-tight">Collections</h1>
          <div className="rule-gold w-32 mx-auto my-10" />
          <p className="max-w-2xl mx-auto text-ink/70 leading-relaxed">Designed in London. Crafted in India. Explore the current LOOXS wardrobe by collection, chapter, or category.</p>
        </div>

        {/* Collections editorial cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-24">
          {collections.map((c, i) => (
            <Link key={c.slug} data-testid={`collection-${c.slug}`} to={`/collections/${c.slug}`} className="group relative aspect-[4/5] md:aspect-[16/10] overflow-hidden hover-shift animate-fade-up" style={{ animationDelay: `${i*80}ms` }}>
              <img src={c.hero_image} alt={c.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-ink/30 group-hover:bg-ink/45 transition-colors duration-700" />
              <div className="absolute bottom-8 left-8 right-8 text-ivory">
                <p className="text-[10px] tracking-luxury uppercase text-gold mb-3">Chapter {c.chapter_roman}</p>
                <h2 className="font-editorial text-4xl md:text-6xl leading-none">{c.name}</h2>
                <p className="mt-3 font-editorial-italic text-lg text-ivory/80">{c.tagline}</p>
              </div>
            </Link>
          ))}
          {/* Gift Cards — collection card in the same visual language */}
          <Link
            data-testid="collection-gift-cards"
            to="/gifts"
            className="group relative aspect-[4/5] md:aspect-[16/10] overflow-hidden hover-shift animate-fade-up md:col-span-2"
            style={{ animationDelay: `${collections.length * 80}ms`,
                     background: "linear-gradient(135deg,#e7d9b8 0%, #f4ead1 40%, #d9c493 100%)" }}
          >
            <div className="absolute inset-0 flex items-center justify-between px-10 md:px-20">
              <div className="max-w-md">
                <p className="text-[10px] tracking-luxury uppercase text-gold mb-3">Instant · Delivered by email</p>
                <h2 className="font-editorial text-5xl md:text-7xl leading-[0.95] tracking-tight text-ink">The Gift Card</h2>
                <p className="mt-4 font-editorial-italic text-lg md:text-2xl text-ink/70">Choose the palette. Set the value. A quiet, considered gift.</p>
                <span className="mt-8 inline-flex items-center gap-3 border-b border-ink pb-1 text-[11px] tracking-widest-plus uppercase text-ink group-hover:gap-5 transition-all">Compose a gift →</span>
              </div>
              <div className="hidden md:flex flex-col gap-2">
                {["#c9a961","#8c6f2c","#efe4c9","#a6835d","#7a5c2c","#dcc48a","#0d1224"].map((hex, idx) => (
                  <span key={idx} className="w-24 h-3 shadow-sm" style={{ background: hex }} />
                ))}
              </div>
            </div>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-6 border-t border-b border-black/10 py-6 mb-16 text-[11px] tracking-widest-plus uppercase">
          <span className="text-black/50">Gender</span>
          {[["","All"],["women","Women"],["men","Men"],["unisex","Unisex"]].map(([v,l]) => (
            <button key={v} data-testid={`filter-gender-${v||'all'}`} onClick={() => setFilter("gender", v)}
              className={`link-draw ${gender === v ? "text-gold" : ""}`}>{l}</button>
          ))}
          <span className="text-black/50 ml-6">Category</span>
          {[["","All"],["t-shirts","T-Shirts"],["blazers","Blazers"],["outerwear","Outerwear"],["trousers","Trousers"],["dresses","Dresses"]].map(([v,l]) => (
            <button key={v} data-testid={`filter-cat-${v||'all'}`} onClick={() => setFilter("category", v)}
              className={`link-draw ${category === v ? "text-gold" : ""}`}>{l}</button>
          ))}
        </div>

        {/* Product grid */}
        {products.length === 0 ? (
          <p className="font-editorial-italic text-2xl text-ink/40 py-16 text-center">No pieces match this selection.</p>
        ) : (
          <div data-testid="collections-grid" className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-16">
            {products.map((p, i) => <ProductTile key={p.id} p={p} i={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductTile({ p, i }) {
  const colors = [...new Set((p.variants || []).map(v => v.color))];
  const secondImg = (p.color_galleries && Object.values(p.color_galleries)[1]?.[0]) || p.images?.[1] || p.images?.[0];
  return (
    <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group animate-fade-up" style={{ animationDelay: `${i*50}ms` }}>
      <div className="relative aspect-[3/4] bg-parchment overflow-hidden">
        <img src={p.images[0]} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:opacity-0" />
        <img src={secondImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        {p.availability !== "in_store" && (
          <span className="absolute top-3 left-3 text-[10px] tracking-widest-plus uppercase bg-ink text-ivory px-2 py-1">
            {p.availability === "pre_order" ? "Pre-order" : "Coming soon"}
          </span>
        )}
      </div>
      <div className="mt-6">
        <p className="text-[10px] tracking-widest-plus uppercase text-gold mb-2">Chapter {p.chapter_roman}</p>
        <p className="font-editorial text-xl leading-tight">{p.name}</p>
        {colors.length > 0 && (
          <div className="mt-2 flex items-center gap-1">
            {colors.slice(0, 5).map(c => (
              <span key={c} className="w-2.5 h-2.5 rounded-full border border-black/20" style={{ backgroundColor: swatchColorTile(c) }} title={c} />
            ))}
            {colors.length > 5 && <span className="text-[9px] tracking-widest-plus uppercase text-black/40 ml-1">+{colors.length - 5}</span>}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between text-[10px] tracking-widest-plus uppercase text-black/50">
          <span>{p.gender}</span>
          <span className="text-black">
            {p.availability === "in_store" ? "In boutique" : p.availability === "pre_order" ? "Pre-order" : "Enquire"}
          </span>
        </div>
      </div>
    </Link>
  );
}

function swatchColorTile(name = "") {
  const n = name.toLowerCase();
  const map = { "black":"#111","ivory":"#f2ead9","sand":"#d9c9a7","navy":"#0b1c34","stone":"#b9ada0","deep olive":"#3c4028","slate grey":"#565c60","dusty rose":"#c5978d","cocoa brown":"#4b352b","obsidian":"#0a0a0a","midnight":"#0d1224" };
  return map[n] || "#8a8a8a";
}
